import "@/App.css";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import PublicLayout from "@/components/PublicLayout";
import DashboardLayout, { Protected } from "@/components/DashboardLayout";

import Landing from "@/pages/Landing";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import PaymentResult from "@/pages/PaymentResult";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import Courses from "@/pages/Courses";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Support from "@/pages/Support";

import EmployerDashboard from "@/pages/employer/EmployerDashboard";
import EmployerJobs from "@/pages/employer/EmployerJobs";
import PostJob from "@/pages/employer/PostJob";
import JobATS from "@/pages/employer/JobATS";
import Pipeline from "@/pages/employer/Pipeline";
import Talent from "@/pages/employer/Talent";
import People from "@/pages/employer/People";
import Documents from "@/pages/employer/Documents";
import EmployerAnalytics from "@/pages/employer/EmployerAnalytics";
import Team from "@/pages/employer/Team";
import Plans from "@/pages/employer/Plans";
import Billing from "@/pages/employer/Billing";

import CandidateDashboard from "@/pages/candidate/CandidateDashboard";
import MyApplications from "@/pages/candidate/MyApplications";
import CvTools from "@/pages/candidate/CvTools";
import MyLearning from "@/pages/candidate/MyLearning";

import PartnerOverview from "@/pages/partner/Overview";
import PartnerOpenings from "@/pages/partner/Openings";
import PartnerSubmissions from "@/pages/partner/Submissions";
import PartnerEarnings from "@/pages/partner/Earnings";
import PartnerCrm from "@/pages/partner/Crm";
import PartnerTeam from "@/pages/partner/Team";
import PartnerSubscription from "@/pages/partner/Subscription";
import InstituteHub from "@/pages/institute/InstituteHub";
import ConsultantDesk from "@/pages/consultant/ConsultantDesk";
import EmployeeWorkspace from "@/pages/employee/EmployeeWorkspace";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminJobs from "@/pages/admin/AdminJobs";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminCourses from "@/pages/admin/AdminCourses";
import AdminPayouts from "@/pages/admin/AdminPayouts";
import AdminPricing from "@/pages/admin/AdminPricing";
import AdminCms from "@/pages/admin/AdminCms";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";

const EMPLOYER_ROLES = ["employer_owner", "employer_admin", "hiring_manager", "interviewer"];
const ADMIN_ROLES = ["super_admin", "admin", "finance_admin", "hr_admin", "support"];

const emp = (el, title) => (
  <Protected roles={EMPLOYER_ROLES}>
    <DashboardLayout title={title}>{el}</DashboardLayout>
  </Protected>
);
const cand = (el, title) => (
  <Protected roles={["candidate"]}>
    <DashboardLayout title={title}>{el}</DashboardLayout>
  </Protected>
);
const adm = (el, title) => (
  <Protected roles={ADMIN_ROLES}>
    <DashboardLayout title={title}>{el}</DashboardLayout>
  </Protected>
);
const ptr = (el, title) => (
  <Protected roles={["franchise_owner", "franchise_recruiter"]}>
    <DashboardLayout title={title}>{el}</DashboardLayout>
  </Protected>
);

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<PublicLayout><Landing /></PublicLayout>} />
            <Route path="/jobs" element={<PublicLayout><Jobs /></PublicLayout>} />
            <Route path="/jobs/:id" element={<PublicLayout><JobDetail /></PublicLayout>} />
            <Route path="/courses" element={<PublicLayout><Courses /></PublicLayout>} />
            <Route path="/blog" element={<PublicLayout><Blog /></PublicLayout>} />
            <Route path="/blog/:slug" element={<PublicLayout><BlogPost /></PublicLayout>} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/payment/success" element={<PaymentResult success />} />
            <Route path="/payment/cancel" element={<PaymentResult />} />
            <Route path="/support" element={
              <Protected>
                <DashboardLayout title="Support"><Support /></DashboardLayout>
              </Protected>} />

            <Route path="/employer" element={emp(<EmployerDashboard />, "Employer Overview")} />
            <Route path="/employer/jobs" element={emp(<EmployerJobs />, "Jobs")} />
            <Route path="/employer/jobs/new" element={emp(<PostJob />, "Post a Job")} />
            <Route path="/employer/jobs/:id" element={emp(<JobATS />, "Job Pipeline")} />
            <Route path="/employer/pipeline" element={emp(<Pipeline />, "ATS Pipeline")} />
            <Route path="/employer/analytics" element={emp(<EmployerAnalytics />, "Hiring Analytics")} />
            <Route path="/employer/talent" element={emp(<Talent />, "Talent Search")} />
            <Route path="/employer/people" element={emp(<People />, "Employees & HRMS")} />
            <Route path="/employer/documents" element={emp(<Documents />, "Document Automation")} />
            <Route path="/employer/team" element={emp(<Team />, "Team Management")} />
            <Route path="/employer/plans" element={emp(<Plans />, "Subscription Plans")} />
            <Route path="/employer/billing" element={emp(<Billing />, "Wallet & Billing")} />

            <Route path="/candidate" element={cand(<CandidateDashboard />, "Candidate Overview")} />
            <Route path="/candidate/applications" element={cand(<MyApplications />, "My Applications")} />
            <Route path="/candidate/cv" element={cand(<CvTools />, "CV & AI Tools")} />
            <Route path="/candidate/learning" element={cand(<MyLearning />, "My Learning")} />

            <Route path="/partner" element={ptr(<PartnerOverview />, "Partner Overview")} />
            <Route path="/partner/openings" element={ptr(<PartnerOpenings />, "Openings")} />
            <Route path="/partner/submissions" element={ptr(<PartnerSubmissions />, "My Submissions")} />
            <Route path="/partner/earnings" element={ptr(<PartnerEarnings />, "Earnings & Payouts")} />
            <Route path="/partner/crm" element={ptr(<PartnerCrm />, "Employer Leads CRM")} />
            <Route path="/partner/team" element={ptr(<PartnerTeam />, "My Team")} />
            <Route path="/partner/subscription" element={ptr(<PartnerSubscription />, "Subscription & Franchise")} />
            <Route path="/institute" element={
              <Protected roles={["institute_owner", "trainer"]}>
                <DashboardLayout title="Institute Hub"><InstituteHub /></DashboardLayout>
              </Protected>} />
            <Route path="/consultant" element={
              <Protected roles={["hr_consultant", "hr_admin"]}>
                <DashboardLayout title="Consultant Desk"><ConsultantDesk /></DashboardLayout>
              </Protected>} />
            <Route path="/employee" element={
              <Protected roles={["candidate", "employee"]}>
                <DashboardLayout title="My Workspace"><EmployeeWorkspace /></DashboardLayout>
              </Protected>} />

            <Route path="/admin" element={adm(<AdminDashboard />, "Command Center")} />
            <Route path="/admin/analytics" element={adm(<AdminAnalytics />, "Platform Analytics")} />
            <Route path="/admin/jobs" element={adm(<AdminJobs />, "Job Reviews")} />
            <Route path="/admin/users" element={adm(<AdminUsers />, "Users & Organizations")} />
            <Route path="/admin/courses" element={adm(<AdminCourses />, "Course Reviews")} />
            <Route path="/admin/payouts" element={adm(<AdminPayouts />, "Payouts")} />
            <Route path="/admin/pricing" element={adm(<AdminPricing />, "Pricing & Configuration")} />
            <Route path="/admin/cms" element={adm(<AdminCms />, "CMS")} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
